/**
 * Comparison data for the /vs pages (site audit D5.3).
 *
 * EVERY COMPETITOR FIGURE HERE WAS READ OFF THAT COMPETITOR'S OWN SITE on the
 * date in `factsCheckedOn`. Do not update these from memory or from a summary —
 * re-open the competitor's pricing page and read it. These are public claims
 * about named companies; a wrong number is worse than no page.
 *
 * `published: false` keeps a page out of the sitemap and the footer and marks
 * it noindex, so a half-filled comparison never reaches Google.
 */

/**
 * @typedef {Record<string, string>} Facts
 * @typedef {{
 *   slug: string,
 *   name: string,
 *   published: boolean,
 *   factsCheckedOn: string,
 *   summary: string,
 *   bestForThem: string,
 *   bestForCardible: string,
 *   facts: Facts
 * }} Competitor
 */

/**
 * Cardible's side of every comparison. All verified against this site.
 * @type {Facts}
 */
export const CARDIBLE_FACTS = {
  artwork: "Unique AI-generated artwork on every card — no templates, ever",
  message: "AI-written in your voice, fully editable before it ships",
  delivery: "We print it as a 4x6 postcard, add postage, and mail it — via our print partner, Lob",
  tracking: "Birthdays and anniversaries kept for every contact",
  autoSend: "No — you send each card, or schedule it up to two years ahead with an optional yearly repeat",
  perCard: "$2.00 - $3.00, postage included",
  plans: "Credit bundles from $15 (credits good for 365 days), or subscriptions from $10/month",
  freeTrial: "First card free, no credit card required",
  business: "Business pilot open — Starter $59/mo (20 cards), Pro $199/mo (100 cards)",
  apps: "iOS and Android",
};

/** Row order for the comparison table. `key` indexes into the facts objects. */
export const COMPARISON_ROWS = [
  { key: "artwork", label: "Card artwork" },
  { key: "message", label: "Message writing" },
  { key: "delivery", label: "Printing and mailing" },
  { key: "tracking", label: "Occasion tracking" },
  { key: "autoSend", label: "Automatic sending" },
  { key: "perCard", label: "Cost per card" },
  { key: "plans", label: "Plans" },
  { key: "freeTrial", label: "Free trial" },
  { key: "business", label: "Business tier" },
  { key: "apps", label: "Mobile apps" },
];

export const competitors = [
  {
    slug: "cardible-vs-postable",
    name: "Postable",
    published: true,
    factsCheckedOn: "8 September 2026",
    summary:
      "Postable is the closest thing to Cardible: a consumer service that prints and mails real cards for you, using designs from professional stationery designers.",
    bestForThem:
      "You would rather browse a curated catalogue of designer cards and pick one yourself, or you are sending a large batch of the same card — Postable's volume pricing is built for that.",
    bestForCardible:
      "You want the artwork and the message generated fresh for each person, and you would like to schedule a whole year of cards in one sitting.",
    facts: {
      artwork: "Pre-made designs from professional stationery designers",
      message: "You write it; they print it in a handwriting-style font",
      delivery: "They print, stamp, and mail it, with domestic postage included",
      tracking: "Address book with birthday reminders; you start each send",
      autoSend: "No — you choose and send each card",
      perCard: "Priced per order and by volume — see postable.com for current rates",
      plans: "Pay per order, no subscription",
      freeTrial: "No free card; free address-collection page",
      business: "Volume and business ordering available",
      apps: "Web, no dedicated iOS or Android app",
    },
  },
  {
    slug: "cardible-vs-handwrytten",
    name: "Handwrytten",
    published: true,
    factsCheckedOn: "8 September 2026",
    summary:
      "Handwrytten is the most established service in the category. Robots hold a real pen and write your note in a handwriting style, aimed heavily at business and CRM use.",
    bestForThem:
      "The note being physically written in real ink matters more to you than the card's artwork, or you need Salesforce, HubSpot or Shopify integration for client outreach.",
    bestForCardible:
      "You want original card artwork rather than a written note on a plain card, you want postage included in the price, and you do not need CRM integration.",
    facts: {
      artwork: "Choose from their card catalogue, or supply your own design",
      message: "Written in real ink by a robot holding a pen, in a handwriting style",
      delivery: "They write, stuff, and mail it — postage billed separately",
      tracking: "Scheduled and recurring sends; CRM integrations drive the timing",
      autoSend: "Yes, via CRM integration and scheduled sends",
      perCard: "Cards retail at $3.75, postage extra; volume discounts to $1.19",
      plans: "Consumer plans from $100/month (24 cards); business plans from $449/month",
      freeTrial: "No free card",
      business: "Extensive — Pro, Enterprise, Elite and Agency tiers with API access",
      apps: "iOS and Android",
    },
  },
  {
    slug: "cardible-vs-felt",
    name: "Felt",
    published: true,
    factsCheckedOn: "8 September 2026",
    summary:
      "Felt is an app for writing a card in your own handwriting on a tablet or phone — you draw the note with a finger or stylus, and Felt prints and mails it.",
    bestForThem:
      "You want your actual handwriting on the card and enjoy writing it out by hand each time. Nothing beats genuinely being your own hand.",
    bestForCardible:
      "You want the card to happen even on the weeks you have no time to sit down and write, and you want the artwork made for the occasion rather than picked from a catalogue.",
    facts: {
      artwork: "Card designs from their catalogue; you add handwriting and stickers",
      message: "You handwrite it yourself with a finger or stylus",
      delivery: "They print, seal, stamp, and mail within 24 hours on weekdays",
      tracking: "Reminders in the app; you write and send each card",
      autoSend: "No — every card is written by hand by you",
      perCard: "$4.50 for a single panel, up to $7.25 for four panels",
      plans: "Felt PLUS from $5/month and Felt PRO from $15/month, both with 3 cards included",
      freeTrial: "No free card",
      business: "Felt PRO adds custom branding and logo replacement",
      apps: "iOS and Android",
    },
  },
];

/**
 * Throws rather than returning undefined: these pages prerender, so a slug typo
 * fails the build instead of shipping a blank comparison.
 *
 * @param {string} slug
 * @returns {Competitor}
 */
export function competitorBySlug(slug) {
  const competitor = competitors.find((c) => c.slug === slug);
  if (!competitor) throw new Error(`Unknown competitor slug: ${slug}`);
  return competitor;
}

export const publishedCompetitors = competitors.filter((c) => c.published);
